/**
 * Stale-bundle detection for long-lived outlet tablets.
 *
 * Incident 2026-08-29: outlet tablets kept running a pre-deploy bundle for
 * hours, so a server-side behaviour change (partner-managed assignment
 * lifecycle) met a client that still wrote the old status. Asset filenames are
 * content-hashed by Vite, so comparing the entry-script URL of the *served*
 * index.html against the one this tab booted with reliably detects staleness.
 *
 * The refresh is a plain reload — never a logout, never a session reset.
 */

/** Entry module URL referenced by an index.html document, if any. */
export function extractEntryHash(html: string): string | null {
  const match = html.match(/<script[^>]+src="([^"]*\/assets\/[^"]+\.js)"/i);
  return match ? match[1] : null;
}

/** Entry module URL of the currently running document. */
export function currentEntryHash(doc: Document = document): string | null {
  const el = doc.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  return el?.getAttribute('src') ?? null;
}

export interface ReloadDecisionInput {
  current: string | null;
  latest: string | null;
  /** Guard so a broken deploy can never put a tab into a reload loop. */
  alreadyReloaded: boolean;
  /** Never interrupt an operator mid-write. */
  hasPendingWork?: boolean;
}

export function shouldReloadForNewBuild(input: ReloadDecisionInput): boolean {
  if (input.alreadyReloaded) return false;
  if (input.hasPendingWork) return false;
  if (!input.current || !input.latest) return false;
  return input.current !== input.latest;
}
